import { NetworkStatus, useLazyQuery, useMutation } from '@apollo/client';
import { Box, ToastAnimateFrom, ToastContent, ToastPosition, ToastType } from '@bechtel/gds';
import {
  CableEnd,
  FeedbackTerminationInput,
  FeedbackTerminationResult,
  MetadataColumn,
  MetadataTableName,
  UiMessageKey,
  UiTextEnum,
} from '@bechtel/sr-types';
import { Formik } from 'formik';
import { find, get, map } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { array, number, object, string } from 'yup';
import { FormSkeleton, FormikValueListener, useAppUser, useLanguage, useTableMetadataContext } from '~/components';
import MessageModal from '~/components/organisms/message-modal/message';
import { FEEDBACK_TERMINATION, SEARCH_CABLE_FROM_TO_TERMS } from '~/gql';
import { usePermissions, useTableMetadataQuery, useToast } from '~/hooks';
import { getDescription } from '~/utilities';
import { RightPanelStyled } from '~/views/commodities/styled';
import { AdditionalInfo } from './additional-info';
import TerminationFeedbackForm from './form';
import { TerminationFeedbackFormikState } from './form/types';
import { terminationFeedbackInitialValues } from './utils';

const getYupSchema = (
  isAlternateLanguageUsed,
  getMultiLingualTextByKey,
  cableColumns: MetadataColumn[],
  cableMarkColumns: MetadataColumn[],
  permission: boolean = false,
  entityText: string = '',
  maxConnections: number = 0
) => {
  const constStatusDescription = getDescription(cableColumns, isAlternateLanguageUsed, 'fromTermConstStatus');
  const tagDescription = getDescription(cableColumns, isAlternateLanguageUsed, 'tag');
  const fromTermConnMadeDescription = getDescription(cableColumns, isAlternateLanguageUsed, 'fromTermConnMade');
  const commodityTypeDescription = getDescription(cableMarkColumns, isAlternateLanguageUsed, 'cableEnd');
  const markDescription = getDescription(cableMarkColumns, isAlternateLanguageUsed, 'mark');
  const markMetadataColumn = find(cableMarkColumns, (c: MetadataColumn) => c.memberName === 'mark');
  const markMinValue = markMetadataColumn && markMetadataColumn.minValue ? markMetadataColumn.minValue : 0;
  const markMaxValue = markMetadataColumn && markMetadataColumn.maxValue ? markMetadataColumn.maxValue : 99999.99;

  const termConstStatus = permission
    ? string()
        .required(getMultiLingualTextByKey(UiTextEnum.VALUE_CANNOT_BE_BLANK, [constStatusDescription]))
        .nullable()
    : string();
  const termConnMade = number()
    .min(
      0,
      getMultiLingualTextByKey(UiTextEnum.VALUE_OUT_OF_RANGE, [
        fromTermConnMadeDescription,
        String(0),
        String(maxConnections),
      ])
    )
    .max(
      maxConnections,
      getMultiLingualTextByKey(UiTextEnum.VALUE_OUT_OF_RANGE, [
        fromTermConnMadeDescription,
        String(0),
        String(maxConnections),
      ])
    );

  return {
    tag: string()
      .required(getMultiLingualTextByKey(UiTextEnum.VALUE_CANNOT_BE_BLANK, [tagDescription]))
      .test(
        'tag-check',
        getMultiLingualTextByKey(UiTextEnum.VALUE_IS_NOT_VALID, [getMultiLingualTextByKey(entityText)]),
        function () {
          const { tagInvalid } = this.parent;
          return tagInvalid === false;
        }
      ),
    commodityType: string()
      .required(getMultiLingualTextByKey(UiTextEnum.VALUE_CANNOT_BE_BLANK, [commodityTypeDescription]))
      .oneOf(
        [CableEnd.FROM, CableEnd.TO] as const,
        getMultiLingualTextByKey(UiTextEnum.VALUE_IS_NOT_VALID, [commodityTypeDescription])
      ),
    termConstStatus,
    termConnMade,
    marks: array().of(
      object().shape({
        fromMark: number()
          .min(
            markMinValue,
            getMultiLingualTextByKey(UiTextEnum.VALUE_OUT_OF_RANGE, [
              markDescription,
              String(markMinValue),
              String(markMinValue),
            ])
          )
          .max(
            markMaxValue,
            getMultiLingualTextByKey(UiTextEnum.VALUE_OUT_OF_RANGE, [
              markDescription,
              String(markMinValue),
              String(markMaxValue),
            ])
          ),
        toMark: number()
          .min(
            markMinValue,
            getMultiLingualTextByKey(UiTextEnum.VALUE_OUT_OF_RANGE, [
              markDescription,
              String(markMinValue),
              String(markMaxValue),
            ])
          )
          .max(
            markMaxValue,
            getMultiLingualTextByKey(UiTextEnum.VALUE_OUT_OF_RANGE, [
              markDescription,
              String(markMinValue),
              String(markMaxValue),
            ])
          ),
      })
    ),
  };
};

const Terminations = () => {
  const appUser = useAppUser();
  const { showToast } = useToast();
  const [initialValues, setInitialValues] = useState<Partial<TerminationFeedbackFormikState>>(
    terminationFeedbackInitialValues
  );
  const tableMetadata = useTableMetadataContext();
  const [getTableMetaData, { data: cableMarkMetadata }] = useTableMetadataQuery();
  const { getMultiLingualTextByKey, isAlternateLanguageUsed } = useLanguage();
  const { canUpdateTerminationFeedback } = usePermissions();
  const [searchCables, { loading, networkStatus, data: searchCableFromToTermData }] = useLazyQuery(
    SEARCH_CABLE_FROM_TO_TERMS,
    {
      notifyOnNetworkStatusChange: true,
    }
  );
  const [commodityType, setCommodityType] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!appUser.userAccount?.defaultProject?.id) return;
    getTableMetaData({
      variables: {
        name: MetadataTableName.CABLE_MARK,
        projectId: appUser.userAccount?.defaultProject?.id,
      },
    });
  }, [appUser.userAccount?.defaultProject?.id, getTableMetaData]);

  const cableFromToTerminationData = get(searchCableFromToTermData, 'searchCables.items[0]');
  const entityText = getMultiLingualTextByKey('Cable');

  const [updateTerminationFeedback, { data: feedbackTerminationData, error: feedbackTerminationError }] = useMutation<
    { feedbackTermination: FeedbackTerminationResult },
    { feedbackTerminationInput: FeedbackTerminationInput }
  >(FEEDBACK_TERMINATION, {
    onCompleted: (data) => {
      const feedbackData = get(data, 'feedbackTermination');
      if (feedbackTerminationError === undefined && data.feedbackTermination.errors == null) {
        showToast(UiTextEnum.SAVED_SUCCESSFULLY_TOAST, [entityText, feedbackData?.cable?.tag ?? ''], ToastType.INFO);
      }
    },
    update: (cache) => {
      cache.evict({ fieldName: 'searchCableMarkRows' });
    },
    onError: () => {
      showToast(UiTextEnum.ERROR_WHILE_SAVING_TOAST, [entityText, ''], ToastType.WARNING);
    },
  });

  const validationSchema = object().shape(
    tableMetadata?.metadataColumns && cableMarkMetadata?.tableMetadata?.metadataColumns
      ? getYupSchema(
          isAlternateLanguageUsed,
          getMultiLingualTextByKey,
          tableMetadata?.metadataColumns,
          cableMarkMetadata?.tableMetadata?.metadataColumns,
          canUpdateTerminationFeedback,
          entityText,
          cableFromToTerminationData?.maxConnections
        )
      : {}
  );

  const saveHandler = async (values) => {
    await updateTerminationFeedback({
      variables: {
        feedbackTerminationInput: { ...values },
      },
    });
  };
  const errorKeys: UiMessageKey[] = get(feedbackTerminationData, 'feedbackTermination.errors') ?? [];

  const onFormikValueChanged = useCallback(
    (_, changedValues) => {
      if (changedValues.commodityType !== commodityType) {
        setCommodityType(changedValues.commodityType);
      }
    },
    [commodityType]
  );
  return (
    <>
      <Formik
        enableReinitialize={true}
        validateOnChange={true}
        validateOnBlur={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          const feedBackTerminationInput: FeedbackTerminationInput = {
            commodityType: values.commodityType === '' ? null : values.commodityType,
            id: searchCableFromToTermData.searchCables.items[0].id,
            termConnMade: parseFloat(values.termConnMade as any),
            termConstStatus: values.termConstStatus === '' ? null : values.termConstStatus,
            termCrimpTool: values.termCrimpTool === '' ? null : values.termCrimpTool,
            termInspectedBy: values.termInspectedBy === '' ? null : values.termInspectedBy,
            termInspectedTime: values.termInspectedTime === '' ? null : values.termInspectedTime,
            termInstalledBy: values.termInstalledBy === '' ? null : values.termInstalledBy,
            termInstalledTime: values.termInstalledTime === '' ? null : values.termInstalledTime,
            cableMarks: map(values.marks, (mark) => {
              return {
                sequenceNumber: mark.sequenceNumber,
                mark: values.commodityType === CableEnd.FROM ? parseFloat(mark.fromMark) : parseFloat(mark.toMark),
              };
            }),
          };
          await saveHandler(feedBackTerminationInput);
          setSubmitting(false);
        }}
      >
        <>
          <FormikValueListener onValueChanged={onFormikValueChanged} />
          <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }}>
            {!tableMetadata ? (
              <FormSkeleton columns={1} rows={8} />
            ) : (
              <TerminationFeedbackForm
                searchCables={searchCables}
                loading={loading || networkStatus === NetworkStatus.refetch}
                cableData={cableFromToTerminationData}
                setInitialValues={setInitialValues}
              />
            )}
          </Box>
        </>
      </Formik>
      {errorKeys && errorKeys.length > 0 && <MessageModal messageKeys={errorKeys}></MessageModal>}
      <ToastContent position={ToastPosition.MIDDLE} animateFrom={ToastAnimateFrom.BOTTOM} />
      <RightPanelStyled isCollapsed={true}>
        <AdditionalInfo
          data={cableFromToTerminationData}
          loading={loading || networkStatus === NetworkStatus.refetch}
          error={feedbackTerminationError}
          commodityType={commodityType}
        />
      </RightPanelStyled>
    </>
  );
};

export default Terminations;
